import { useEffect, useReducer } from "react";
import { ReactSubscriber } from "utils/ReactSubscriber";

export interface ContactStore {
  id: number;
  name: string;
}

type StorageState = ContactStore[];

class LocalContactsStorage extends ReactSubscriber<StorageState> {
  private key = "openContacts";

  constructor() {
    super([]);
    const valueInSessionStorage = window.sessionStorage.getItem(this.key);
    if (valueInSessionStorage == null) {
      return;
    }
    this.state = JSON.parse(valueInSessionStorage) as StorageState;
  }

  setState(state: StorageState) {
    this.state = state;
    window.sessionStorage.setItem(this.key, JSON.stringify(this.state));
    this.updateState();
  }
}

class LocalFavoriteContactsStorage extends ReactSubscriber<StorageState> {
  private key = "favoriteContacts";

  constructor() {
    super([]);
    const valueInLocalStorage = window.localStorage.getItem(this.key);
    if (valueInLocalStorage == null) {
      return;
    }
    this.state = JSON.parse(valueInLocalStorage) as StorageState;
  }

  setState(state: StorageState) {
    this.state = state;
    window.localStorage.setItem(this.key, JSON.stringify(this.state));
    this.updateState();
  }
}

const localFavoriteContactsStorage = new LocalFavoriteContactsStorage();

export const useFavoriteContactsStore = (): [
  StorageState,
  (newState: StorageState) => void
] => {
  const [, forceRender] = useReducer((previous) => previous + 1, 0);

  useEffect(() => {
    const index = localFavoriteContactsStorage.subscribe(forceRender);
    return () => localFavoriteContactsStorage.unsubscribe(index);
  }, []);

  const state = localFavoriteContactsStorage.State;

  const setState = (newState: StorageState) => {
    localFavoriteContactsStorage.setState(newState);
  };

  return [state, setState];
};

const localContactsStorage = new LocalContactsStorage();

export const useLocalStorageStore = (): [
  StorageState,
  (newState: StorageState) => void
] => {
  const [, forceRender] = useReducer((previous) => previous + 1, 0);

  useEffect(() => {
    const index = localContactsStorage.subscribe(forceRender);
    return () => localContactsStorage.unsubscribe(index);
  }, []);

  const state = localContactsStorage.State;

  const setState = (newState: StorageState) => {
    localContactsStorage.setState(newState);
  };

  return [state, setState];
};
